import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, ClipboardList, MessagesSquare, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import ExistingCards from "./ExistingCards";
import HabitsForm from "./HabitsForm";
import HabitsChat from "./HabitsChat";
import CardResults from "./CardResults";
import { errMsg } from "./util";

const Step = ({ n, title, sub, children, testId }) => (
  <section className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-baseline gap-3 mb-4"><span className="font-display font-black text-2xl text-[#D4AF37]">{n}</span><div><div className="font-display font-black text-lg">{title}</div><div className="text-xs text-stone-500">{sub}</div></div></div>
    {children}
  </section>
);

/** Full Card Lens flow for one customer: their cards → habits (form / chat / statement) → ranked Crece cards. */
export default function CardLensPanel({ clientId, customer, fresh = false }) {
  const [meta, setMeta] = useState(null);
  const [s, setS] = useState(null);
  const [mode, setMode] = useState("form");
  const [busy, setBusy] = useState(false);
  const poll = useRef();

  useEffect(() => { api.get("/card-lens/meta").then((r) => setMeta(r.data)); }, []);
  const start = async () => {
    const { data } = await api.post("/card-lens/sessions", { client_id: clientId || null, client_name: customer?.name || null, client_mobile: customer?.mobile || null });
    setS(data);
  };
  const booted = useRef(null);
  useEffect(() => {
    const k = `${clientId}-${fresh}`;
    if (booted.current === k) return undefined;
    booted.current = k;
    (async () => {
      try {
        if (clientId && !fresh) {
          const { data } = await api.get("/card-lens/sessions", { params: { client_id: clientId } });
          if (data[0]) { const full = await api.get(`/card-lens/sessions/${data[0].id}`); setS(full.data); return; }
        }
        await start();
      } catch (e) { toast.error(errMsg(e, "Couldn't start Card Lens")); }
    })();
    return () => clearTimeout(poll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, fresh]);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { const { data } = await api.put(`/card-lens/sessions/${s.id}`, patch); setS((cur) => ({ ...cur, ...Object.fromEntries(Object.keys(patch).map((k) => [k, data[k]])), updated_at: data.updated_at })); return data; }
    catch (e) { toast.error(errMsg(e, "Save failed")); return null; }
  };
  const isPending = (r) => r?.pending && (r.pending.existing || r.pending.reasons);
  const watch = (id, n = 0) => {
    clearTimeout(poll.current);
    poll.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/card-lens/sessions/${id}`);
        setS((cur) => ({ ...cur, result: data.result, status: data.status }));
        if (isPending(data.result) && n < 30) watch(id, n + 1);
      } catch { /* keep showing what we have */ }
    }, 5000);
  };
  const persistForm = () => save({ spends: s.spends || {}, preferences: s.preferences || [], income_annual: s.income_annual, age: s.age, fee_tolerance: s.fee_tolerance });
  const analyze = async () => {
    setBusy(true);
    const ok = await persistForm();
    if (!ok) return setBusy(false);
    try {
      const { data } = await api.post(`/card-lens/sessions/${s.id}/analyze`);
      setS((cur) => ({ ...cur, status: data.status, result: data.result }));
      if (isPending(data.result)) watch(s.id);
    } catch (e) { toast.error(errMsg(e, "Couldn't analyse")); }
    setBusy(false);
  };

  if (!meta || !s) return <div className="py-12 text-center" data-testid="cl-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0B4D3A]" /></div>;
  const processing = busy;
  return (
    <div className="space-y-4" data-testid="card-lens-panel">
      {s.client_name && <div className="flex items-center gap-2 text-xs text-stone-500" data-testid="cl-session-customer">For <b className="text-stone-800">{s.client_name}</b>{s.client_mobile ? ` · ${s.client_mobile}` : ""}
        <button onClick={() => { setS(null); start(); }} className="ml-auto inline-flex items-center gap-1 font-bold text-[#0B4D3A]" data-testid="cl-new-session"><RotateCcw className="w-3 h-3" /> New analysis</button></div>}
      <Step n="1" title="Cards they already hold" sub="From Customer 360, typed in, or read off a statement" testId="cl-step-cards">
        <ExistingCards session={s} onSave={(cards) => save({ existing_cards: cards })} onSession={setS} beforeUpload={persistForm} />
      </Step>
      <Step n="2" title="How they spend" sub="Fill the quick form, or let the advisor chat ask them" testId="cl-step-habits">
        <div className="flex gap-1 bg-stone-100 rounded-full p-1 w-fit mb-4" data-testid="cl-habit-mode">
          {[["form", "Quick form", ClipboardList], ["chat", "Chat", MessagesSquare]].map(([v, l, I]) => (
            <button key={v} onClick={() => { if (v !== mode) persistForm(); setMode(v); }} data-testid={`cl-mode-${v}`} className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-4 py-1.5 transition-colors ${mode === v ? "bg-white text-[#0B4D3A] shadow-sm" : "text-stone-500"}`}><I className="w-3.5 h-3.5" /> {l}</button>
          ))}
        </div>
        {mode === "chat" ? <HabitsChat session={s} onSession={setS} /> : <HabitsForm meta={meta} value={s} onChange={(v) => setS(v)} />}
        {s.spends_source === "statement" && <div className="text-[11px] text-[#0B4D3A] font-bold mt-2" data-testid="cl-spends-from-statement">Spends pre-filled from the uploaded statement. Edit if needed.</div>}
      </Step>
      <div className="flex justify-end">
        <Button onClick={analyze} disabled={processing} className="rounded-full bg-[#0B4D3A] hover:bg-[#083B2D] h-11 px-8 font-bold" data-testid="cl-analyze-btn">
          {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{processing ? "Ranking Crece cards…" : "Find best cards"}
        </Button>
      </div>
      {s.status === "done" && !processing && <CardResults session={s} onSession={setS} />}
    </div>
  );
}
